import { z } from "zod";
import { Cents, Id, IsoDate, IsoDateTime } from "../common.ts";
import { endpoint } from "../endpoint.ts";
import { LegalForm } from "../company.ts";

/**
 * Shared accounting domain: the accountant and the mission (lettre de mission),
 * and the EXPERTISE ADMIN billing grid. Screen-specific contracts live next to
 * this file (banking, taxes, documents, social, discussions, exercises, dossier,
 * settings) and are re-exported from contracts/accounting.ts.
 */

/** "YYYY-MM" accounting month. */
export const YearMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

export const Accountant = z.object({
  id: Id,
  firstName: z.string(),
  lastName: z.string(),
  /** "Expert-comptable" */
  title: z.string(),
  email: z.email(),
  firm: z.string(),
  /** Shown in thread headers: "réponse sous 24 h ouvrées". */
  responseDelay: z.string(),
  /** Office hours shown in Aide: "Lun – ven · 9 h – 18 h". */
  hours: z.string(),
});
export type Accountant = z.infer<typeof Accountant>;

export const MissionStatus = z.enum(["active", "pending_signature", "suspended"]);
export type MissionStatus = z.infer<typeof MissionStatus>;

export const Mission = z.object({
  status: MissionStatus,
  startedAt: IsoDate,
  letterSignedAt: IsoDate.nullable(),
  /** Document id of the signed engagement letter (Documents › permanents). */
  letterDocumentId: Id.nullable(),
  accountant: Accountant,
});
export type Mission = z.infer<typeof Mission>;

/* ───────────────────────── Billing grid (EXPERTISE ADMIN) ───────────────────────── */

/**
 * Two grids, by regime (client price list, all amounts HT, VAT 20 % on top):
 * - "company": SASU / SAS / EURL / SARL, 79 € HT/month up to 20 bank transactions … 799 € HT up to 400;
 * - "micro": auto-entrepreneur (legal form "AE"), 9 € HT … 199 € HT.
 * 20 tiers of 20 transactions each. Every month is billed at the tier its actual transaction count falls
 * into (the invoice can go up or down). Above 400: on quote (invoiced by EXPERTISE ADMIN, never by Melrys).
 * This is the single source of the numbers: the site copy (content/site/pricing.ts) and the mocks read it.
 */
export const AccountingRegime = z.enum(["company", "micro"]);
export type AccountingRegime = z.infer<typeof AccountingRegime>;

/** "micro" for the auto-entrepreneur (AE), "company" for every company form. */
export function regimeOf(legalForm: LegalForm): AccountingRegime {
  return legalForm === "AE" ? "micro" : "company";
}

export const TIER_BAND = 20;
export const TIER_COUNT = 20;
export const GRID_CEILING = TIER_BAND * TIER_COUNT;

/** Monthly price per tier, in euros HT (tier i covers up to 20 × i transactions). */
const GRID_EUROS: Record<AccountingRegime, readonly number[]> = {
  company: [79, 99, 119, 139, 179, 209, 239, 269, 299, 349, 379, 419, 459, 499, 549, 599, 649, 699, 749, 799],
  micro: [9, 19, 29, 39, 49, 59, 69, 79, 89, 99, 109, 119, 129, 139, 149, 159, 169, 179, 189, 199],
};

export const Tier = z.object({ index: z.number().int().min(1).max(TIER_COUNT), maxTransactions: z.number().int(), priceCents: Cents });
export type Tier = z.infer<typeof Tier>;

export const ACCOUNTING_GRIDS: Record<AccountingRegime, Tier[]> = {
  company: GRID_EUROS.company.map((eur, i) => ({ index: i + 1, maxTransactions: TIER_BAND * (i + 1), priceCents: eur * 100 })),
  micro: GRID_EUROS.micro.map((eur, i) => ({ index: i + 1, maxTransactions: TIER_BAND * (i + 1), priceCents: eur * 100 })),
};

/** Tier for a monthly transaction count in a regime's grid, or null above the grid (quote). */
export function tierFor(regime: AccountingRegime, transactions: number): Tier | null {
  return ACCOUNTING_GRIDS[regime].find((t) => transactions <= t.maxTransactions) ?? null;
}

/** The tier after `tier` in the same grid, or null on the last one. */
export function nextTierOf(regime: AccountingRegime, tier: Tier | null): Tier | null {
  return tier ? (ACCOUNTING_GRIDS[regime][tier.index] ?? null) : null;
}

export const AccountingGrids = z.object({
  /** The 20 tiers of each regime, by ascending cap. */
  grids: z.object({ company: z.array(Tier), micro: z.array(Tier) }),
  /** Width of a tier (20 transactions). */
  band: z.number().int(),
  /** Last cap of both grids (400); above it, on quote. */
  ceiling: z.number().int(),
});
export type AccountingGrids = z.infer<typeof AccountingGrids>;

/**
 * Creation bundled with Melrys accounting (« Création si compta », boards 39/40): the client pays the
 * creation-only fee at creation, then the first month of accounting carries a one-off discount equal to the
 * difference, so the creation effectively costs AE 49 € / SASU-EURL 99 € HT.
 */
export const FIRST_ACCOUNTING_MONTH_DISCOUNT_CENTS: Record<AccountingRegime, number> = { micro: 3000, company: 5000 };

/** A line deducted from an accounting invoice (the bundle's first-month discount). */
export const InvoiceDiscount = z.object({
  /** « Remise création + comptabilité · 1er mois » */
  label: z.string(),
  /** Positive amount deducted, HT: FIRST_ACCOUNTING_MONTH_DISCOUNT_CENTS, capped at the month's tier price. */
  amountCents: Cents,
});
export type InvoiceDiscount = z.infer<typeof InvoiceDiscount>;

export const NextInvoice = z.object({
  /** The partner firm issues the accounting invoice; EXPERTISE ADMIN collects it on its behalf (decision of 30/10). */
  issuer: z.literal("HM Audit Expertise"),
  /** Month being counted. */
  month: YearMonth,
  /** Grid used: from the company's legal form. */
  regime: AccountingRegime,
  transactionsThisMonth: z.number().int(),
  /** "grid": amount from the tier grid; "quote": above 400, no amount. */
  pricing: z.enum(["grid", "quote"]),
  tier: Tier.nullable(),
  nextTier: Tier.nullable(),
  /** Tier price before any discount; null on quote. */
  grossAmountCents: Cents.nullable(),
  /** First month after a creation bundled with accounting, otherwise null. */
  discount: InvoiceDiscount.nullable(),
  /** Amount debited (gross − discount, never below 0 since the discount is capped); null on quote. */
  amountCents: Cents.nullable(),
  debitDate: IsoDate,
});
export type NextInvoice = z.infer<typeof NextInvoice>;

/**
 * "Vous allez bientôt changer de palier" window (board 6): opens once per tier
 * and per month when the month's count reaches 90 % of the tier cap.
 */
export const TierNotice = z.object({
  show: z.boolean(),
  kind: z.enum(["next_tier", "last_tier"]).nullable(),
  month: YearMonth,
  transactions: z.number().int(),
  regime: AccountingRegime,
  current: Tier.nullable(),
  next: Tier.nullable(),
});
export type TierNotice = z.infer<typeof TierNotice>;

export const accountingCoreApi = {
  mission: endpoint({ id: "accounting.mission", method: "GET", path: "/accounting/mission", response: z.object({ mission: Mission.nullable() }) }),
  /**
   * The two fee grids (company / micro), readable by any signed-in user, with or without accounting: activation
   * screens, onboarding, creation upsell and the menu quote them (« dès 79 € HT / mois », « dès 9 € HT / mois »
   * for an auto-entrepreneur). Pick the grid with `regimeOf(company.legalForm)`. Static per release; cache it.
   */
  tiers: endpoint({ id: "accounting.billing.tiers", method: "GET", path: "/accounting/billing/tiers", response: AccountingGrids }),
  nextInvoice: endpoint({ id: "accounting.billing.nextInvoice", method: "GET", path: "/accounting/billing/next-invoice", response: NextInvoice }),
  tierNotice: endpoint({ id: "accounting.billing.tierNotice", method: "GET", path: "/accounting/billing/tier-notice", response: TierNotice }),
  /** Closes the tier window: it won't come back before the next tier or next month. */
  ackTierNotice: endpoint({
    id: "accounting.billing.tierNotice.ack",
    method: "POST",
    path: "/accounting/billing/tier-notice/ack",
    body: z.object({ month: YearMonth, tierIndex: z.number().int() }),
    response: z.object({ ok: z.literal(true), acknowledgedAt: IsoDateTime }),
  }),
};
